import { createSlice } from '@reduxjs/toolkit'

const FILE_TYPES = {
  AIR: 'air',
  BOARDING_PASS: 'boarding_pass',
  ITINERARY: 'itinerary',
  ENTRY_STAMP: 'entry_stamp',
  CRUISE: 'cruise',
  CRUISE_SCHEDULE: 'cruise_schedule',
  ARN: 'arn',
  GIVEBACK_APPLICATION: 'giveback_application'
}

const OCR_FIELDS = {
  CRUISE_NAME: 'cruise_name',
  SHIP_NAME: 'ship_name',
  FIRST_NAME: 'first_name',
  LAST_NAME: 'last_name',
  DATE_OF_VISIT: 'date_of_visit'
}

const VERIFY_FIELDS = {
  CRUISE_NAME: 'cruise_name',
  CRUISE_PORT: 'cruise_port',
  FIRST_NAME: 'first_name',
  LAST_NAME: 'last_name',
  SHIP_NAME: 'ship_name',
  DATE_OF_VISIT: 'date_of_visit'
}

// Helper functions
const createOcrField = () => ({
  ocr: 0,
  score: '',
  manual: 0
})

const createVerifyField = () => false

const createOcrObject = () => {
  const ocr = {}
  Object.values(OCR_FIELDS).forEach(field => {
    ocr[field] = createOcrField()
  })
  return ocr
}

const createVerifyObject = () => {
  const verify = {}
  Object.values(VERIFY_FIELDS).forEach(field => {
    verify[field] = createVerifyField()
  })
  return verify
}

const createFilesObject = () => ({
  [FILE_TYPES.AIR]: [],
  [FILE_TYPES.CRUISE]: [],
  [FILE_TYPES.CRUISE_SCHEDULE]: [],
  [FILE_TYPES.ITINERARY]: [],
  [FILE_TYPES.BOARDING_PASS]: [],
  [FILE_TYPES.ENTRY_STAMP]: [],
  [FILE_TYPES.ARN]: [],
  [FILE_TYPES.GIVEBACK_APPLICATION]: []
})

// Initial state structure
const initialState = {
  // Attributes from API
  air_sea_type: '',
  id: '',
  name: '',
  date_entered: '',
  date_modified: '',
  modified_user_id: '',
  created_by: '',
  description: '',
  deleted: '0',
  assigned_user_id: '',
  cruise_name: '',
  date_of_visit: '',
  transport_number: '',
  route_of_stay: '',
  transport_company: '',
  way_of_transport: '',
  code: '',
  town: '',
  exit_point: '',
  way_of_exit: '',
  ship_name: '',
  contact_id: '',
  date_of_birth: '',
  type_of_trip: '',
  passport_number: '',
  send_secure_link: '0',
  send_itinerary_secure_link: '0',
  cruise_itinerary_secure_link: '0',
  send_cruise_id_secure_link: '0',
  cruise_port: '',
  send_fmm_secure_link: '0',
  send_entry_stamp_secure_link: '0',
  cruise_verified: '0',
  send_cruise_schedule_secure_link: '0',
  first_name: '',
  last_name: '',
  sea_arn_clicked: false,
  // Files structure (keeping existing structure)
  files: createFilesObject(),
  // Additional arrays
  airlines: [],
  cruises: [],
  // OCR data
  ocr: createOcrObject(),
  // Verification data
  verify: createVerifyObject()
}

// Helper functions for better code organization
const createFileEntry = (file_obj, base64) => ({
  file_obj,
  base64
})

export const tripseaSlice = createSlice({
  name: 'TripSea',
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    setTripSea: (state, action) => {
      return { ...state, ...action.payload }
    },
    setTripSeaFromAPI: (state, action) => {
      const tripSeaData = action.payload
      if (tripSeaData) {
        // Merge all properties except files and verify
        const { files, verify: incomingVerify, ...otherData } = tripSeaData
        Object.assign(state, otherData)

        // Properly merge files if they exist
        if (files) {
          // Ensure files object exists and merge each file type
          if (!state.files) {
            state.files = createFilesObject()
          }

          // Merge each file type individually
          Object.keys(files).forEach(fileType => {
            if (files[fileType] && Array.isArray(files[fileType])) {
              state.files[fileType] = files[fileType]
            }
          })
        }

        // Merge verify flags into default structure; do not overwrite with empty object
        if (!state.verify || typeof state.verify !== 'object') {
          state.verify = createVerifyObject()
        }
        if (incomingVerify && typeof incomingVerify === 'object') {
          Object.keys(state.verify).forEach(key => {
            if (Object.prototype.hasOwnProperty.call(incomingVerify, key)) {
              state.verify[key] = Boolean(incomingVerify[key])
            }
          })
        }

        return state
      }
    },
    addNewFileTripSea: (state, action) => {
      const { file_type } = action.payload
      state.files[file_type].push({})
      return state
    },
    removeFileTripSea: (state, action) => {
      const { file_type, file_no } = action.payload
      state.files[file_type].splice(file_no, 1)
      return state
    },
    initializeTripSea: (state, action) => {
      return initialState
    },
    setTripSeaFile: (state, action) => {
      const { file_type, file_no, file_obj, base64 } = action.payload
      state.files[file_type][file_no] = createFileEntry(file_obj, base64)
      return state
    },
    setTripSeaFileID: (state, action) => {
      const { file_type, file_no, id, document_revision_id } = action.payload
      const fileEntry = state.files[file_type][file_no]

      fileEntry.id = id

      if (document_revision_id) {
        fileEntry.file_obj.document_revision_id = document_revision_id
      }

      return state
    },
    setAllTripFiles: (state, action) => {
      const { file_type, files } = action.payload
      state.files[file_type] = files
      return state
    },
    createNewTripSea: (state, action) => {
      return {
        ...initialState,
        id: '',
        air_sea_type: 'sea'
      }
    },
    addTripSeaFileWithData: (state, action) => {
      const { file_type, file } = action.payload
      if (state.files[file_type]) {
        // Always add the file, don't check length
        state.files[file_type].push(file)
      }
      return state
    },
    seaVerifyAll: (state, action) => {
      // Handle both direct boolean payload and object with value property
      const verificationValue =
        typeof action.payload === 'boolean'
          ? action.payload
          : action.payload.value || action.payload
      const verifyKeys = Object.keys(state.verify)

      verifyKeys.forEach(key => {
        state.verify[key] = verificationValue
      })

      return state
    },
    seaVerifySingleField: (state, action) => {
      const { field, value } = action.payload
      state.verify[field] = value
      return state
    },
    setAllSeaTripFiles: (state, action) => {
      const { file_type, files } = action.payload
      state.files[file_type] = files
      return state
    },
    setRejectAll: (state, action) => {
      const { value, fields } = action.payload
      const verifyKeys = Object.keys(state.verify)

      verifyKeys.forEach(key => {
        if (fields.includes(key)) {
          state.verify[key] = value
        }
      })

      return state
    },
    updateTripSeaOcrField: (state, action) => {
      const { field, data } = action.payload
      state.ocr[field] = {
        ...state.ocr[field],
        ...data
      }
      return state
    },
    setTripSeaFromAPICustomerModal: (state, action) => {
      const tripSeaData = action.payload;
      if (tripSeaData) {
        return { ...state, ...tripSeaData };
      }
    }
  }
})

export const {
  setTripSea,
  setTripSeaFromAPI,
  addNewFileTripSea,
  seaVerifyAll,
  removeFileTripSea,
  setTripSeaFile,
  initializeTripSea,
  setTripSeaFileID,
  setAllTripFiles,
  createNewTripSea,
  setAllSeaTripFiles,
  addTripSeaFileWithData,
  seaVerifySingleField,
  setRejectAll,
  updateTripSeaOcrField,
  setTripSeaFromAPICustomerModal
} = tripseaSlice.actions

export const selectTripSea = state => state?.tripSea

export default tripseaSlice.reducer
